import { useEffect, useMemo, useState } from 'react';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { CloseButton } from '~/components/ui/CloseButton';
import { Text } from '~/components/ui/Text';
import { TextInput } from '~/components/ui/TextInput';
import { rage } from '~/rage';
import { RewardType, VehicleTuningCategory } from '~/shared';
import {
	ContentContainer,
	HeaderContainer,
	PanelContainer,
	StyledInputGroup,
	StyledInputLabel,
	StyledOptionalLabel,
	StyledRewardCard,
	Title,
} from '../DonateItemLayout.styled';
import { DonateItemMultiSelect } from '../DonateItemMultiSelect';
import {
	getFilterPlaceholder,
	getRewardLabel,
	getRewardPlaceholder,
	getSelectPlaceholder,
	isSearchableType,
	REWARD_TYPE_OPTIONS,
} from '../donateItemRewardHelpers';
import { DonateItemSelect } from '../DonateItemSelect';
import { createDonateItemRow } from '../donateItemUtils';
import {
	useDonateItemNumberFormat,
	useDonateItemRewardOptions,
} from '../useDonateItemOptions';

const SALE_CURRENCY_OPTIONS = [
	{ value: 'FALSE', label: 'Рубли' },
	{ value: 'TRUE', label: 'Донат' },
];

const DonateItem: React.FC = () => {
	const { formatNumber, parseFormattedNumber, filterNumericOnly } =
		useDonateItemNumberFormat();
	const { getOptionsForRewardType, getTuningKitOptions, getTuningItemOptions } =
		useDonateItemRewardOptions();
	const [rewardType, setRewardType] = useState<RewardType>(RewardType.ITEM);
	const [rewardValue, setRewardValue] = useState('');
	const [rewardCount, setRewardCount] = useState('1');
	const [tuningKit, setTuningKit] = useState('');
	const [tuningItems, setTuningItems] = useState<string[]>([]);
	const [sellAmount, setSellAmount] = useState('');
	const [sellIsDonate, setSellIsDonate] = useState(false);

	const value =
		rewardType === RewardType.MONEY || rewardType === RewardType.EXP
			? parseFormattedNumber(rewardValue)
			: rewardValue;
	const amount = parseFormattedNumber(sellAmount);
	const row = useMemo(
		() =>
			createDonateItemRow({
				type: rewardType,
				value,
				count: parseFormattedNumber(rewardCount),
				tuningKit,
				tuningItems,
				sellAmount: amount,
				sellIsDonate,
			}),
		[
			amount,
			parseFormattedNumber,
			rewardCount,
			rewardType,
			sellIsDonate,
			tuningItems,
			tuningKit,
			value,
		],
	);

	useEffect(() => {
		rage.input
			.setAnyInputFocused(true)
			.catch((err) => rage.notifications.showException(err));

		return () => {
			rage.input
				.setAnyInputFocused(false)
				.catch((err) => rage.notifications.showException(err));
		};
	}, []);

	const handleTypeChange = (type: RewardType) => {
		setRewardType(type);
		setRewardValue('');
		setRewardCount('1');
		setTuningKit('');
		setTuningItems([]);
	};

	const handleRewardValueChange = (value: string) => {
		if (rewardType !== RewardType.MONEY && rewardType !== RewardType.EXP) {
			setRewardValue(value);
			return;
		}

		const numericValue = filterNumericOnly(value);
		setRewardValue(
			numericValue && !Number.isNaN(parseInt(numericValue, 10))
				? formatNumber(parseInt(numericValue, 10))
				: '',
		);
	};

	const handleSellAmountChange = (value: string) => {
		const numericValue = filterNumericOnly(value);
		setSellAmount(
			numericValue && !Number.isNaN(parseInt(numericValue, 10))
				? formatNumber(parseInt(numericValue, 10))
				: '',
		);
	};

	const handleCopy = () => {
		if (!row) {
			return;
		}

		rage.input.copyToClipboard(row);
		rage.notifications.showInfo('Строка скопирована');
	};

	return (
		<PanelContainer>
			<HeaderContainer>
				<Title>Создание донат-предмета</Title>
				<CloseButton
					height={ButtonHeight.Height30}
					onClick={() => rage.donateItem.exitMenu()}
				/>
			</HeaderContainer>
			<ContentContainer>
				<StyledRewardCard>
					<StyledInputGroup>
						<StyledInputLabel>Тип награды</StyledInputLabel>
						<DonateItemSelect
							options={REWARD_TYPE_OPTIONS}
							value={rewardType}
							onChange={(type) => handleTypeChange(type as RewardType)}
							showOptionValue={false}
						/>
					</StyledInputGroup>
					<StyledInputGroup>
						<StyledInputLabel>{getRewardLabel(rewardType)}</StyledInputLabel>
						{isSearchableType(rewardType) ? (
							<DonateItemSelect
								options={getOptionsForRewardType(rewardType)}
								value={rewardValue}
								onChange={(value) => {
									setRewardValue(value);
									if (rewardType === RewardType.VEHICLE) {
										setTuningKit('');
										setTuningItems([]);
									}
								}}
								placeholder={getSelectPlaceholder(rewardType)}
								filterPlaceholder={getFilterPlaceholder(rewardType)}
							/>
						) : (
							<TextInput
								value={rewardValue}
								onChange={(event) =>
									handleRewardValueChange(event.target.value)
								}
								placeholder={getRewardPlaceholder(rewardType)}
								height={40}
								fontSize={16}
							/>
						)}
					</StyledInputGroup>
					{rewardType === RewardType.VEHICLE && rewardValue && (
						<>
							<StyledInputGroup>
								<StyledInputLabel>
									Набор тюнинга
									<StyledOptionalLabel>необязательно</StyledOptionalLabel>
								</StyledInputLabel>
								<DonateItemSelect
									options={[
										{ value: '', label: 'Без набора тюнинга' },
										...getTuningKitOptions(rewardValue),
									]}
									value={tuningKit}
									onChange={setTuningKit}
									placeholder="Выберите набор тюнинга..."
									filterPlaceholder="Поиск набора тюнинга..."
								/>
							</StyledInputGroup>
							<StyledInputGroup>
								<StyledInputLabel>
									Элементы тюнинга
									<StyledOptionalLabel>необязательно</StyledOptionalLabel>
								</StyledInputLabel>
								<DonateItemMultiSelect
									options={getTuningItemOptions(rewardValue)}
									values={tuningItems}
									onChange={setTuningItems}
									placeholder="Выберите детали..."
									filterPlaceholder="Поиск детали..."
									singleSelectCategory={VehicleTuningCategory.RIMS}
								/>
							</StyledInputGroup>
						</>
					)}
					{rewardType === RewardType.ITEM && (
						<StyledInputGroup>
							<StyledInputLabel>Количество</StyledInputLabel>
							<TextInput
								value={rewardCount}
								onChange={(event) => {
									const numericValue = filterNumericOnly(event.target.value);
									setRewardCount(
										numericValue && !Number.isNaN(parseInt(numericValue, 10))
											? formatNumber(parseInt(numericValue, 10))
											: '',
									);
								}}
								placeholder="1"
								height={40}
								fontSize={16}
							/>
						</StyledInputGroup>
					)}
					<StyledInputGroup>
						<StyledInputLabel>
							Цена продажи
							<StyledOptionalLabel>необязательно</StyledOptionalLabel>
						</StyledInputLabel>
						<TextInput
							value={sellAmount}
							onChange={(event) => handleSellAmountChange(event.target.value)}
							placeholder="Пусто, если предмет не продаётся"
							height={40}
							fontSize={16}
						/>
					</StyledInputGroup>
					<StyledInputGroup>
						<StyledInputLabel>Валюта продажи</StyledInputLabel>
						<DonateItemSelect
							options={SALE_CURRENCY_OPTIONS}
							value={sellIsDonate ? 'TRUE' : 'FALSE'}
							onChange={(value) => setSellIsDonate(value === 'TRUE')}
							placeholder="Выберите валюту..."
							filterPlaceholder="Поиск валюты..."
						/>
					</StyledInputGroup>
				</StyledRewardCard>
				<Button
					color={ButtonColors.Select}
					height={ButtonHeight.Height50}
					onClick={handleCopy}
					disabled={!row}
				>
					<Text>Скопировать</Text>
				</Button>
			</ContentContainer>
		</PanelContainer>
	);
};

export { DonateItem };
